import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import {
  FiBarChart2,
  FiBell,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiHome,
  FiLogOut,
  FiMenu,
  FiUser,
} from 'react-icons/fi'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { AddAdminUserModal } from '../components/AddAdminUser'
import { ProfileEdit } from '../components/dashboard/ProfileEdit'
import { isUserAdmin, supabase } from '../lib/supabase'

const baseNavigation = [
  { name: 'Dashboard', href: 'dashboard', icon: FiBarChart2 },
  { name: 'Acomodações', href: 'properties', icon: FiHome },
  { name: 'Reservas', href: 'reservations', icon: FiCalendar },
  // { name: "Clientes", href: "clients", icon: FiUsers },
  { name: 'Proprietários', href: 'owners', icon: FiUser },
  { name: 'Relatórios', href: 'reports', icon: FiFileText },
]

export function DashboardLayout() {
  const navigate = useNavigate()
  const [userProfile, setUserProfile] = useState<{
    name?: string
    email?: string
  } | null>(null)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false) // Changed to use isAdmin state
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isProfileOpen,
    onOpen: onProfileOpen,
    onClose: onProfileClose,
  } = useDisclosure()
  const {
    isOpen: isAddAdminOpen,
    onOpen: onAddAdminOpen,
    onClose: onAddAdminClose,
  } = useDisclosure()

  useEffect(() => {
    fetchUserProfile()
    checkAdminStatus()
  }, [])

  const checkAdminStatus = async () => {
    const adminStatus = await isUserAdmin()
    setIsAdmin(adminStatus)
  }

  const fetchUserProfile = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('name, email')
        .eq('id', user.id)
        .single()

      setUserProfile(
        profile
          ? {
              name: profile.name || undefined,
              email: profile.email || undefined,
            }
          : null
      )
    } catch (error) {
      console.error('Error fetching user profile:', error)
    }
  }

  const handleSignOut = () => {
    supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  const NavItem = ({
    item,
    isMobile = false,
  }: {
    item: { name: string; href: string; icon: React.ElementType }
    isMobile?: boolean
  }) => (
    <NavLink to={item.href} end={item.href === 'dashboard' || item.href === ''}>
      {({ isActive }) => (
        <Button
          leftIcon={<Icon as={item.icon} />}
          variant={isActive ? 'solid' : 'ghost'}
          colorScheme={isActive ? 'blue' : 'gray'}
          justifyContent="flex-start"
          width="full"
          _hover={{ bg: 'gray.100' }}
          onClick={() => {
            if (isMobile) onClose()
          }}
        >
          {(isMobile || !isCollapsed) && item.name}
        </Button>
      )}
    </NavLink>
  )

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Desktop Header */}
      <Box
        display={{ base: 'none', lg: 'block' }}
        position="fixed"
        left={isCollapsed ? '16' : '72'}
        right={0}
        top={0}
        height="16"
        bg="white"
        borderBottomWidth="1px"
        borderColor="gray.200"
        zIndex={20}
        transition="left 0.2s"
      >
        <Box h="full" px={6}>
          <Box maxW="none" h="full">
            <Flex justify="space-between" align="center" h="full">
              <Text fontSize="sm" color="gray.500">
                Última atualização: {new Date().toLocaleString('pt-BR')}
              </Text>
              <HStack spacing={4}>
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Notifications"
                    icon={<FiBell />}
                    variant="ghost"
                    position="relative"
                    size="md"
                  ></MenuButton>
                  <MenuList>
                    <MenuItem>Nenhuma notificação por enquanto</MenuItem>
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    p={1}
                    rounded="md"
                    _hover={{ bg: 'gray.100' }}
                  >
                    <HStack>
                      <Avatar
                        size="sm"
                        name={userProfile?.name}
                        src={undefined}
                        icon={<FiUser />}
                      />
                      <Box
                        display={{ base: 'none', md: 'block' }}
                        textAlign="left"
                      >
                        <Text fontSize="sm" fontWeight="medium">
                          {userProfile?.name || 'Usuário'}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {userProfile?.email}
                        </Text>
                      </Box>
                    </HStack>
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      icon={<Icon as={FiUser} boxSize={4} />}
                      onClick={onProfileOpen}
                    >
                      Perfil
                    </MenuItem>
                    {isAdmin && (
                      <MenuItem
                        icon={<Icon as={FiUser} boxSize={4} />}
                        onClick={onAddAdminOpen}
                      >
                        Add Administrador
                      </MenuItem>
                    )}
                    <Divider />
                    <MenuItem
                      icon={<Icon as={FiLogOut} boxSize={4} />}
                      onClick={handleSignOut}
                    >
                      Sair
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </Flex>
          </Box>
        </Box>
      </Box>

      {/* Mobile header */}
      <Flex
        display={{ base: 'flex', lg: 'none' }}
        position="fixed"
        top={0}
        left={0}
        right={0}
        alignItems="center"
        justifyContent="space-between"
        px={4}
        py={2}
        bg="white"
        borderBottomWidth="1px"
        borderColor="gray.200"
        zIndex={20}
        height="16"
      >
        <IconButton
          aria-label="Open menu"
          icon={<FiMenu />}
          onClick={onOpen}
          variant="ghost"
          size="md"
        />
        <VStack spacing={0}>
          <Image
            src="/Untitled.webp"
            alt="Infinity Logo"
            h="32px"
            objectFit="contain"
          />
          <Text fontSize="xs" fontWeight="semibold" color="gray.600">
            Sistema de Gestão
          </Text>
        </VStack>
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<FiUser />}
            variant="ghost"
            aria-label="User menu"
            size="md"
          />
          <MenuList>
            <MenuItem
              icon={<Icon as={FiUser} boxSize={4} />}
              onClick={onProfileOpen}
            >
              Perfil
            </MenuItem>
            {isAdmin && (
              <MenuItem
                icon={<Icon as={FiUser} boxSize={4} />}
                onClick={onAddAdminOpen}
              >
                Add Administrador
              </MenuItem>
            )}
            <Divider />
            <MenuItem
              icon={<Icon as={FiLogOut} boxSize={4} />}
              onClick={handleSignOut}
            >
              Sair
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      {/* Mobile drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" py={3}>
            <VStack spacing={1} align="center">
              <Image
                src="/Untitled.webp"
                alt="Infinity Logo"
                h="32px"
                objectFit="contain"
              />
              <Text fontSize="md" fontWeight="semibold" color="gray.700">
                Sistema de Gestão
              </Text>
            </VStack>
          </DrawerHeader>
          <DrawerBody mt={4}>
            <Stack spacing={3}>
              {[
                ...baseNavigation,
                // ...(isAdmin
                //   ? [
                //       {
                //         name: 'Auditoria',
                //         href: 'audit',
                //         icon: FiFileText,
                //       },
                //     ]
                //   : []),
              ].map((item) => (
                <NavItem key={item.name} item={item} isMobile />
              ))}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Desktop sidebar */}
      <Box
        display={{ base: 'none', lg: 'block' }}
        position="fixed"
        left={0}
        w={isCollapsed ? '16' : '72'}
        top={0}
        h="full"
        bg="white"
        borderRightWidth="1px"
        borderColor="gray.200"
        zIndex={30}
        transition="width 0.2s"
        overflowY="auto"
      >
        <Flex direction="column" h="full">
          <VStack
            spacing={2}
            align={isCollapsed ? 'center' : 'flex-start'}
            mb={8}
            px={isCollapsed ? 2 : 6}
            py={4}
            borderBottomWidth="1px"
            borderColor="gray.200"
          >
            <Image
              src="/Untitled.webp"
              alt="Infinity Logo"
              h="40px"
              objectFit="contain"
              mx={isCollapsed ? 'auto' : '0'}
            />
            {!isCollapsed && (
              <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                Sistema de Gestão
              </Text>
            )}
          </VStack>
          <Stack spacing={2} flex="1" px={isCollapsed ? 2 : 4} py={2}>
            {[
              ...baseNavigation,
              // ...(isAdmin
              //   ? [
              //       {
              //         name: 'Auditoria',
              //         href: 'audit',
              //         icon: FiFileText,
              //       },
              //     ]
              //   : []),
            ].map((item) => (
              <NavItem key={item.name} item={item} />
            ))}
          </Stack>
          <Box
            borderTopWidth="1px"
            borderColor="gray.200"
            p={isCollapsed ? 2 : 4}
          >
            <IconButton
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              icon={isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
              onClick={() => setIsCollapsed(!isCollapsed)}
              variant="outline"
              className={!isCollapsed ? 'w-full' : ''}
              size="sm"
              alignSelf={isCollapsed ? 'center' : 'flex-end'}
            >
              {!isCollapsed && (isCollapsed ? '' : 'Recolher')}
            </IconButton>
          </Box>
        </Flex>
      </Box>

      {/* Main content */}
      <Box
        ml={{ base: 0, lg: isCollapsed ? '16' : '72' }}
        mt={{ base: 16, lg: 16 }}
        flexGrow={1}
        zIndex={10}
        transition="margin-left 0.2s"
        width={{ lg: `calc(100% - ${isCollapsed ? '64px' : '288px'})` }}
        minH="calc(100vh - 64px)"
      >
        <Outlet />
      </Box>

      {/* Profile Edit Modal */}
      <ProfileEdit
        isOpen={isProfileOpen}
        onClose={() => {
          onProfileClose()
          fetchUserProfile()
        }}
      />

      {/* Add Admin User Modal */}
      <AddAdminUserModal isOpen={isAddAdminOpen} onClose={onAddAdminClose} />
    </Box>
  )
}
